import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Fish,
  Save,
  CreditCard,
  Calendar,
  Award,
  Lock,
  Eye,
  EyeOff
} from "lucide-react";

const FISHING_TYPES = ["Fresh Water", "Specimen", "Bass", "Fly Fishing", "Rock and Surf", "Boat", "Offshore", "Estuary"];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    cell_number: "",
    area: "",
    fishing_types: []
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        surname: user.surname || "",
        cell_number: user.cell_number || "",
        area: user.area || "",
        fishing_types: user.fishing_types || []
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleFishingType = (type) => {
    const current = formData.fishing_types;
    if (current.includes(type)) {
      setFormData({ ...formData, fishing_types: current.filter(t => t !== type) });
    } else {
      setFormData({ ...formData, fishing_types: [...current, type] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/auth/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await refreshUser();
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.current_password || !passwordData.new_password) {
      toast.error("Please fill in all password fields");
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully!");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to change password";
      toast.error(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <Layout title="My Profile" subtitle="Manage your account details">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Membership Card Display */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className={`gradient-ocean border-0 overflow-hidden ${user?.is_member_of_month ? "ring-4 ring-yellow-400" : ""}`}>
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center">
                    <span className="text-3xl font-bold text-white">
                      {user?.name?.[0]}{user?.surname?.[0]}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-2xl font-bold text-white">{user?.name} {user?.surname}</h2>
                      {user?.is_member_of_month && (
                        <Award className="w-6 h-6 text-yellow-400" />
                      )}
                    </div>
                    <p className="text-white/70">{user?.email}</p>
                    <p className="font-mono text-[var(--sunrise-orange)] mt-2">{user?.member_number}</p>
                  </div>
                </div>
                
                <div className="text-left md:text-right">
                  <Badge className={user?.subscription_status === "active" ? "bg-[var(--success)]" : "bg-yellow-500"}>
                    {user?.subscription_status === "active" ? "Active Member" : "Pending"}
                  </Badge>
                  <p className="text-white/70 text-sm mt-2">
                    {user?.subscription_type && `${user.subscription_type.charAt(0).toUpperCase() + user.subscription_type.slice(1)} Plan`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Edit Profile Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-white card-shadow border-0">
            <CardHeader>
              <CardTitle className="text-[var(--deep-ocean)]">Edit Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">First Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="profile-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="surname">Surname</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="surname"
                        name="surname"
                        value={formData.surname}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="profile-surname-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="cell_number">Cell Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="cell_number"
                        name="cell_number"
                        value={formData.cell_number}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="profile-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="area">Area</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="area"
                        name="area"
                        value={formData.area}
                        onChange={handleChange}
                        className="input-field pl-10"
                        data-testid="profile-area-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Label>Fishing Types</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {FISHING_TYPES.map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => toggleFishingType(type)}
                        className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          formData.fishing_types.includes(type)
                            ? "border-[var(--deep-ocean)] bg-[var(--deep-ocean)]/10 text-[var(--deep-ocean)]"
                            : "border-gray-200 text-[var(--river-stone)] hover:border-[var(--deep-ocean)]/50"
                        }`}
                        data-testid={`profile-fishing-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                  data-testid="save-profile-btn"
                >
                  {loading ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Subscription Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-white card-shadow border-0">
            <CardHeader>
              <CardTitle className="text-[var(--deep-ocean)]">Subscription Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[var(--deep-ocean)]/10 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-[var(--deep-ocean)]" />
                  </div>
                  <div>
                    <p className="text-sm text-[var(--river-stone)]">Plan</p>
                    <p className="font-semibold text-[var(--deep-ocean)]">
                      {user?.subscription_type ? `${user.subscription_type.charAt(0).toUpperCase() + user.subscription_type.slice(1)}` : "None"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[var(--deep-ocean)]/10 flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-[var(--deep-ocean)]" />
                  </div>
                  <div>
                    <p className="text-sm text-[var(--river-stone)]">Member Since</p>
                    <p className="font-semibold text-[var(--deep-ocean)]">
                      {user?.member_since ? new Date(user.member_since).toLocaleDateString() : "-"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[var(--deep-ocean)]/10 flex items-center justify-center">
                    <Fish className="w-6 h-6 text-[var(--deep-ocean)]" />
                  </div>
                  <div>
                    <p className="text-sm text-[var(--river-stone)]">Expires</p>
                    <p className="font-semibold text-[var(--deep-ocean)]">
                      {user?.subscription_end ? new Date(user.subscription_end).toLocaleDateString() : "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {user?.subscription_status !== "active" && (
                <div className="mt-6 p-4 bg-[var(--sunrise-orange)]/10 rounded-lg">
                  <p className="text-[var(--deep-ocean)] mb-3">
                    Activate your membership to access all benefits!
                  </p>
                  <Button
                    onClick={() => navigate("/subscription")}
                    className="btn-accent"
                    data-testid="activate-subscription-btn"
                  >
                    Choose a Plan
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Change Password */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-white card-shadow border-0">
            <CardHeader>
              <CardTitle className="text-[var(--deep-ocean)]">Change Password</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Current Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                    <Input
                      id="current_password"
                      name="current_password"
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                      className="input-field pl-10 pr-10"
                      placeholder="Enter current password"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--river-stone)] hover:text-[var(--deep-ocean)]"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="new_password"
                        name="new_password"
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        className="input-field pl-10 pr-10"
                        placeholder="Min 6 characters"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--river-stone)] hover:text-[var(--deep-ocean)]"
                      >
                        {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--river-stone)]" />
                      <Input
                        id="confirm_password"
                        name="confirm_password"
                        type={showNewPassword ? "text" : "password"}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                        className="input-field pl-10"
                        placeholder="Confirm new password"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="outline"
                  className="border-[var(--deep-ocean)] text-[var(--deep-ocean)] hover:bg-[var(--deep-ocean)] hover:text-white"
                  disabled={passwordLoading}
                  data-testid="change-password-btn"
                >
                  {passwordLoading ? (
                    <span className="spinner" />
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </Layout>
  );
};

export default ProfilePage;
